import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Grid, Group, Loader, Text } from '@mantine/core';
import { Button, Card } from '@xplatform/design-system';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ExtractionResults, ImageViewer, UploadSection } from '@features/index';
import { useExtraction, useDoctypes } from '@hooks/useContext';
import { useBrowserNotifications } from '@src/hooks/useBrowserNotifications';
import { Toast } from '@src/components';
import type { ExtractionOptions } from '@src/hooks/extraction/useExtraction';
import type { ReviewItemState } from '@src/hooks/extraction/useResultsManagement';
import { REVIEW_ITEM_PARAM, REVIEW_PROCESS_PARAM } from '@utils/reviewLink';
import './ExtractionPage.scss';
import type { SharePointSelection } from '@src/services/sharepointApi';

const ExtractionPage: React.FC = () => {
    const { doctypeCode: doctypeCodeParam } = useParams<{ doctypeCode: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const reviewProcessId = searchParams.get(REVIEW_PROCESS_PARAM);
    const reviewItemId = searchParams.get(REVIEW_ITEM_PARAM);
    const isReviewMode = Boolean(reviewProcessId && reviewItemId);
    const { requestPermission } = useBrowserNotifications();
    const {
        toast,
        hideToast,
        doctypeCode,
        setDoctypeCode,
        resetState,
        processResultsInfo,
        openProcessItem,
    } = useExtraction();

    const {
        fetchDoctypes,
        doctypes,
    } = useDoctypes();

    const [uploadedFile, setUploadedFile] = useState<File>();
    const [selectedSharePointFolder, setSelectedSharePointFolder] = useState<SharePointSelection>();
    const [extractionOptions, setExtractionOptions] = useState<ExtractionOptions>({});
    const lastProcessIdRef = useRef<string>('');
    const [reviewState, setReviewState] = useState<ReviewItemState | { kind: 'loading' }>({ kind: 'loading' });

    useEffect(() => {
        setUploadedFile(undefined);
        setSelectedSharePointFolder(undefined);
        setExtractionOptions({});
    }, [doctypeCode]);

    useEffect(() => {
        if (!doctypes.find((doctype) => doctype.docTypeCode === doctypeCode)?.sharepointEnabled) {
            setSelectedSharePointFolder(undefined);
        }
    }, [doctypeCode, doctypes]);

    useEffect(() => {
        requestPermission();
    }, [requestPermission]);

    useEffect(() => {
        fetchDoctypes();
    }, [fetchDoctypes]);

    // Set doctypeCode from URL param
    useEffect(() => {
        if (doctypeCodeParam) {
            setDoctypeCode(doctypeCodeParam);
        }
    }, [doctypeCodeParam, setDoctypeCode]);

    // Cleanup on unmount only
    useEffect(() => {
        return () => {
            resetState();
            setDoctypeCode('');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run cleanup only on unmount, not when resetState changes

    // Review link: load the one document once the context runs on the URL's doctype
    // (setDoctypeCode resets the extraction state, so loading earlier would be wiped).
    useEffect(() => {
        if (!isReviewMode || !reviewProcessId || !reviewItemId || !doctypeCodeParam) return;
        if (doctypeCode !== doctypeCodeParam) return;
        let cancelled = false;
        setReviewState({ kind: 'loading' });
        openProcessItem(reviewProcessId, reviewItemId, doctypeCodeParam).then((state) => {
            if (!cancelled) setReviewState(state);
        });
        return () => {
            cancelled = true;
        };
    }, [isReviewMode, reviewProcessId, reviewItemId, doctypeCode, doctypeCodeParam, openProcessItem]);

    useEffect(() => {
        if (processResultsInfo.length === 0) return;

        const latestProcess = processResultsInfo[0];

        if (latestProcess.id !== lastProcessIdRef.current) {
            const isCompleted =
                latestProcess.status === 'COMPLETED' ||
                latestProcess.status === 'PARTIAL_COMPLETED' ||
                latestProcess.status === 'FAILED';

            if (isCompleted) {
                lastProcessIdRef.current = latestProcess.id;
                setUploadedFile(undefined);
                setSelectedSharePointFolder(undefined);
                setExtractionOptions({});
            }
        }
    }, [processResultsInfo]);

    if (isReviewMode) {
        const backToDocuments = () => navigate({ search: '' });
        const reviewMessage = (() => {
            switch (reviewState.kind) {
                case 'notFound':
                    return t('extraction.review.notFound');
                case 'wrongDoctype':
                    return t('extraction.review.wrongDoctype', { doctype: reviewState.doctypeCode });
                case 'notReady':
                    return t('extraction.review.notReady');
                case 'error':
                    return reviewState.message;
                default:
                    return null;
            }
        })();

        return (
            <div className="extraction-page extraction-page--review">
                <Group justify="space-between" wrap="nowrap" className="review-header">
                    <Group gap="sm" wrap="nowrap">
                        <Button
                            size="xs"
                            variant="subtle"
                            leftSection={<IconArrowLeft size={16} />}
                            onClick={backToDocuments}
                        >
                            {t('extraction.review.backToDocuments')}
                        </Button>
                        {reviewState.kind === 'ready' && (
                            <Text fw={600} truncate="end">{reviewState.item.fileName}</Text>
                        )}
                    </Group>
                </Group>

                {reviewState.kind === 'ready' && reviewState.item.errorMessage && (
                    <Alert color="red" icon={<IconAlertCircle size={16} />}>
                        {reviewState.item.errorMessage}
                    </Alert>
                )}

                <div className="review-body">
                    {reviewState.kind === 'loading' && (
                        <div className="review-placeholder"><Loader size={48} /></div>
                    )}
                    {reviewMessage && (
                        <Card className="review-placeholder">
                            <Text fw={600}>{t('extraction.review.unavailableTitle')}</Text>
                            <Text size="sm" c="dimmed">{reviewMessage}</Text>
                            {reviewState.kind === 'wrongDoctype' && (
                                <Button
                                    size="xs"
                                    onClick={() => navigate(
                                        { pathname: `../${encodeURIComponent(reviewState.doctypeCode)}`, search: searchParams.toString() },
                                        { relative: 'path' },
                                    )}
                                >
                                    {t('extraction.review.openInDoctype', { doctype: reviewState.doctypeCode })}
                                </Button>
                            )}
                        </Card>
                    )}
                    {reviewState.kind === 'ready' && (
                        <Grid className="review-grid">
                            <Grid.Col span={{ base: 12, md: 6 }}>
                                <ImageViewer reviewMode />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 6 }}>
                                <ExtractionResults />
                            </Grid.Col>
                        </Grid>
                    )}
                </div>
                <Toast toast={toast} onClose={hideToast} />
            </div>
        );
    }

    return (
        <Grid className="extraction-page">
            <Grid.Col span={{ base: 12, md: 2 }}>
                <UploadSection
                    uploadedFile={uploadedFile}
                    extractionOptions={extractionOptions}
                    onFileUpload={(file) => {
                        setUploadedFile(file);
                        setSelectedSharePointFolder(undefined);
                    }}
                    selectedSharePointFolder={selectedSharePointFolder}
                    onFolderSelect={(folder) => {
                        setSelectedSharePointFolder(folder);
                        if (folder) setUploadedFile(undefined);
                    }}
                    onOptionsChange={setExtractionOptions}
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 5 }}>
                <ImageViewer
                    uploadedFile={uploadedFile}
                    extractionOptions={extractionOptions}
                    onFileUpload={(file) => {
                        setUploadedFile(file);
                        if (file) setSelectedSharePointFolder(undefined);
                    }}
                    selectedSharePointFolder={selectedSharePointFolder}
                    onFolderSelect={setSelectedSharePointFolder}
                    onOptionsChange={setExtractionOptions}
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 5 }}>
                <ExtractionResults />
            </Grid.Col>
            <Toast toast={toast} onClose={hideToast} />
        </Grid>
    );
};

export default ExtractionPage;
